import { NavLink, useNavigate } from "react-router-dom";
import NotificationBell from "@/components/NotificationBell";
import { LayoutDashboard, FolderKanban, Users, Package, Settings, LogOut, Menu, X, Inbox, Truck, Calculator } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useState } from "react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/leads",     label: "Leads",     icon: Inbox,           testid: "nav-leads" },
  { to: "/projects",  label: "Projects",  icon: FolderKanban,    testid: "nav-projects" },
  { to: "/products",  label: "Materials", icon: Package,         testid: "nav-materials" },
  { to: "/clients",   label: "Clients",   icon: Users,           testid: "nav-clients" },
  { to: "/settings",  label: "Settings",  icon: Settings,        testid: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border flex-col" style={{ background: "#F4F2EC" }}>
        <div className="px-7 pt-9 pb-8">
          <div className="text-[10px] tracking-[0.32em] font-bold" style={{ color: "#A68A64", lineHeight: 1.05 }}>SUBTEND</div>
          <div className="text-[10px] tracking-[0.32em] font-bold text-foreground mt-1" style={{ lineHeight: 1.05 }}>STUDIO</div>
        </div>
        <nav className="px-3 flex-1 space-y-0.5">
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm tracking-wide rounded-sm transition-colors ${
                  isActive ? "bg-white text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
                }`}>
              <Icon strokeWidth={1.5} className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-2">
          <NotificationBell/>
        </div>
        <div className="px-5 py-5 border-t border-border">
          <div className="text-xs font-medium truncate" data-testid="user-name">{user?.name || user?.email}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <button data-testid="btn-logout" onClick={doLogout} className="mt-4 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 border-b border-border" style={{ background: "rgba(249,248,246,0.92)", backdropFilter: "blur(12px)" }}>
        <div className="flex items-center justify-between px-5 h-14">
          <div>
            <div className="text-[10px] tracking-[0.28em] font-bold" style={{ color: "#A68A64", lineHeight: 1.05 }}>SUBTEND</div>
            <div className="text-[10px] tracking-[0.28em] font-bold text-foreground mt-0.5" style={{ lineHeight: 1.05 }}>STUDIO</div>
          </div>
          <button data-testid="btn-mobile-menu" onClick={() => setMobileOpen(true)} className="p-2"><Menu strokeWidth={1.5} /></button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative ml-auto w-72 h-full p-6 flex flex-col" style={{ background: "#F4F2EC" }}>
            <div className="flex justify-between items-center mb-8">
              <div className="font-serif text-2xl">Menu</div>
              <button data-testid="btn-close-menu" onClick={() => setMobileOpen(false)}><X strokeWidth={1.5} /></button>
            </div>
            <nav className="space-y-1 flex-1">
              {nav.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`${testid}-mobile`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm rounded-sm ${isActive ? "bg-white font-medium" : "text-muted-foreground"}`}>
                  <Icon strokeWidth={1.5} className="w-4 h-4" /> {label}
                </NavLink>
              ))}
            </nav>
            <button data-testid="btn-logout-mobile" onClick={doLogout} className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0 page-anim">
        {children}
        <footer className="mt-24 py-6 border-t border-border text-[10px] uppercase tracking-widest text-muted-foreground flex justify-center gap-6" data-testid="app-footer">
          <a href="/terms" data-testid="footer-link-terms" className="hover:text-foreground">Terms</a>
          <a href="/privacy" data-testid="footer-link-privacy" className="hover:text-foreground">Privacy</a>
        </footer>
      </main>
    </div>
  );
}
